import React, { useRef } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { useExpectedMoveInDateFilter } from '../ExpectedMoveInDateFilter';
import { FilterConfig, FilterKey } from '../shared/types';
import { FILTER_ICONS, FILTER_LABELS } from '../shared/constants';
import { usePriceFilter } from './usePriceFilter';
import { useBedroomFilter } from './useBedroomFilter';

export function useFiltersConfig() {
  // Anchor refs
  const priceAnchorRef = useRef<HTMLDivElement>(null);
  const bedsAnchorRef = useRef<HTMLDivElement>(null);
  const verifiedAnchorRef = useRef<HTMLDivElement>(null);
  const topRatedAnchorRef = useRef<HTMLDivElement>(null);
  const rentStabilizedAnchorRef = useRef<HTMLDivElement>(null);
  const goodCauseAnchorRef = useRef<HTMLDivElement>(null);
  const moveInDateAnchorRef = useRef<HTMLDivElement>(null);

  // Store selectors
  const buildingStatus = useStore(s => s.listingsFilters.buildingStatus);
  const price = useStore(s => s.listingsFilters.price);
  const bedrooms = useStore(s => s.listingsFilters.bedrooms);
  const expectedMoveInDate = useStore(s => s.listingsFilters.expectedMoveInDate);

  // Hooks for display formatting
  const { formatPriceDisplay } = usePriceFilter();
  const { formatBedroomsDisplay } = useBedroomFilter();
  const { formatExpectedMoveInDateDisplay } = useExpectedMoveInDateFilter();

  const filters: FilterConfig[] = [
    {
      key: 'price',
      label: formatPriceDisplay() || FILTER_LABELS.price,
      anchorRef: priceAnchorRef,
      isActive: !!price.min || !!price.max,
    },
    {
      key: 'beds',
      label: formatBedroomsDisplay() || FILTER_LABELS.beds,
      anchorRef: bedsAnchorRef,
      isActive: !!bedrooms.length,
    },
    {
      key: 'verified',
      label: FILTER_LABELS.verified,
      anchorRef: verifiedAnchorRef,
      icon: React.createElement(FILTER_ICONS.verified, { className: 'w-4 h-4' }),
      isActive: !!buildingStatus.isVerified,
    },
    {
      key: 'topRated',
      label: FILTER_LABELS.topRated,
      anchorRef: topRatedAnchorRef,
      icon: React.createElement(FILTER_ICONS.topRated, { className: 'w-4 h-4' }),
      isActive: !!buildingStatus.isTopRated,
    },
    {
      key: 'rentStabilized',
      label: FILTER_LABELS.rentStabilized,
      anchorRef: rentStabilizedAnchorRef,
      icon: React.createElement(FILTER_ICONS.rentStabilized, { className: 'w-4 h-4' }),
      isActive: !!buildingStatus.isRentStabilized,
    },
    {
      key: 'goodCause',
      label: FILTER_LABELS.goodCause,
      anchorRef: goodCauseAnchorRef,
      icon: React.createElement(FILTER_ICONS.goodCause, { className: 'w-4 h-4' }),
      isActive: !!buildingStatus.hasBuildingGoodCauseEviction,
    },
    {
      key: 'moveInDate',
      label: formatExpectedMoveInDateDisplay() || FILTER_LABELS.moveInDate,
      anchorRef: moveInDateAnchorRef,
      isActive: !!expectedMoveInDate.min || !!expectedMoveInDate.max,
    },
  ];

  return { filters };
}

export type { FilterKey, FilterConfig };
